import { Container, Loader, Tabs } from '@itds/elements/index.js';
import React, { useEffect, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';

import AgencyForms from '@brokerage/pages/agencies/AgencyForms';
import AnnuityQuoter from './annuity_quoters/Show';
import ApiClient from '@brokerage/pages/agencies/api_clients/ApiClient';
import BgaQuoterShow from './agency_life_quoters/Show';
import { Show as ViveShow } from '@brokerage/pages/agencies/api_clients/Show';
import { Show as BillingShow } from '@brokerage/pages/agencies/api_clients/Show';
import { Show as FormEnginesShow } from '@brokerage/pages/agencies/form_engines/Show';
import General from '@brokerage/pages/agencies/General';
import H1 from '@brokerage/components/H1';
import Integrations from '@brokerage/pages/agencies/Integrations';
import { List as LeadsList } from '@brokerage/pages/agencies/leads/List';
import { Show as LeadsShow } from '@brokerage/pages/agencies/leads/Show';
import LtcQuotersShow from './ltc_quoters/Show';
import MembersList from '@brokerage/pages/agencies/members/List';
import { Show as MobleAppShow } from '@brokerage/pages/agencies/mobile_apps/Show';
import Notification from '@brokerage/components/Notification';
import NotificationTriggers from '@brokerage/pages/agencies/NotificationTriggers';
import Products from '@brokerage/pages/agencies/Products';
import Website from '@brokerage/pages/agencies/websites/Show';
import { Show as WebsiteQuotersShow } from '@brokerage/pages/agencies/website_quoters/Show';
import UniversalQuoterShow from './universal_quoters/Show';

import { AgencyContext } from '@brokerage/contexts/AgencyContext';
import { useNotification, ACTION_MESSAGES } from '@brokerage/utils/custom_hooks/useNotification';

import { Api } from '@brokerage/utils/Api';

const AgencyShow = () => {
  const params = useParams();

  const [agency, setAgency] = useState({});
  const [billing_profile, setBillingProfile] = useState({});
  const [loading, setLoading] = useState(false);
  const [member_emails, setMemberEmails] = useState([]);
  const [member_names, setMemberNames] = useState([]);
  const [non_agency_members, setNonAgencyMembers] = useState([]);
  const [bga_quoter_products, setBgaQuotersProducts] = useState([]);
  const [mobile_app_products, setMobileAppsProducts] = useState([]);
  const [website_quoter_products, setWebsiteQuotersProducts] = useState([]);
  const { notification, setNotification } = useNotification({ type: null, message: '' });
  const [nestedNewMember, setNestedNewMember] = useState(false);

  const demo_tabs = [
    {
      isActive: window.location.hash.includes('general'),
      label: 'General',
      to: 'general'
    },
    {
      isActive: window.location.hash.includes('integrations'),
      label: 'Integrations',
      to: 'integrations'
    },
    {
      isActive: window.location.hash.includes('products'),
      label: 'Products',
      to: 'products'
    },
    {
      isActive: window.location.hash.includes('agency_forms'),
      label: 'Agency Forms',
      to: 'agency_forms'
    },
    {
      isActive: window.location.hash.includes('leads'),
      label: 'Leads',
      to: 'leads'
    },
    {
      isActive: window.location.hash.includes('agency_members'),
      label: 'Members',
      to: 'agency_members'
    },
    {
      isActive: window.location.hash.includes('notification_triggers'),
      label: 'Notification Triggers',
      to: 'notification_triggers'
    }
  ];

  const billing_tab = {
    isActive: window.location.hash.includes('billing'),
    label: 'Billing',
    to: 'billing'
  };

  useEffect(() => {
    loadAgency();
    loadMembers();
    getMemberAgencyProducts();
  }, []);

  useEffect(() => {
    // display info message if !billing_profile.stripe_customer
    if (agency.is_demo === false && !billing_profile.stripe_customer) {
      context.setNotification(ACTION_MESSAGES.BASE.STRIPE.NO_STRIPE_ACCOUNT);
    } else if (
      agency.is_demo === false &&
      billing_profile.stripe_customer &&
      !billing_profile.stripe_customer.default_source
    ) {
      context.setNotification(ACTION_MESSAGES.BASE.STRIPE.NO_PAYMENT_METHOD);
    } else if (
      agency.is_demo === false &&
      billing_profile.stripe_customer &&
      billing_profile.stripe_customer.delinquent
    ) {
      context.setNotification(ACTION_MESSAGES.BASE.STRIPE.LAST_CHARGE_FAILED);
    } else context.setNotification(ACTION_MESSAGES.NULL_MESSAGE);

    return () => {
      // clear info message on unmount
      context.setNotification(ACTION_MESSAGES.NULL_MESSAGE);
    };
  }, [billing_profile]);

  // TODO: what are these used for?
  const loadMembers = () => {
    Api.loadMembers({ agency_id: params.agency_id, hide_associated_data: true }).then(response => {
      const { data } = response;
      const member_ids = data.map(m => m.id);

      const member_emails = data.map(member => {
        return {
          value: member.id,
          label: member.email
        };
      });

      const member_names = data.map(member => {
        return {
          value: member.id,
          label: `${member.first_name + ' ' + member.last_name}`
        };
      });

      Api.loadMembers({ hide_associated_data: true }).then(response => {
        const filtered_members = response.data.filter(m => !member_ids.includes(m.id));
        const member_options = filtered_members.map(m => ({
          value: m.id,
          label: `${m.email} (${m.first_name} ${m.last_name})`
        }));

        setNonAgencyMembers(member_options);
      });

      setMemberEmails(member_emails);
      setMemberNames(member_names);
    });
  };

  const loadAgency = () => {
    setLoading(true);
    Api.loadAgency(params.agency_id).then(response => {
      const { data } = response;

      setAgency(data);

      if (data.billing_profile_guid) {
        Api.loadAgencyBillingProfile(data.billing_profile_guid).then(({ data }) => {
          setBillingProfile(data);

          setLoading(false);
        });
      } else {
        context.setNotification(ACTION_MESSAGES.BASE.STRIPE.NO_STRIPE_ACCOUNT);
        setLoading(false);
      }
    });
  };

  const getMemberAgencyProducts = () => {
    Promise.all([
      Api.loadBgaQuoters(params.agency_id),
      Api.loadWebsiteQuoters(params.agency_id),
      Api.loadAgencyMobileApps(params.agency_id)
    ]).then(response => {
      setBgaQuotersProducts(response[0].data);
      setWebsiteQuotersProducts(response[1].data);
      setMobileAppsProducts(response[2].data);
    });
  };

  const context = {
    agency,
    billing_profile,
    bga_quoter_products,
    loadAgency,
    loading,
    loadMembers,
    member_emails,
    member_names,
    non_agency_members,
    mobile_app_products,
    setLoading,
    website_quoter_products,
    notification,
    setNotification,
    nestedNewMember,
    setNestedNewMember
  };

  return (
    <AgencyContext.Provider value={context}>
      {loading ? (
        <Loader />
      ) : (
        <Container>
          <Notification {...context.notification} />

          <H1>{`${agency?.name || ' '} ${agency?.is_demo ? '(Demo)' : ''}`}</H1>

          <Tabs tabs={agency?.stripe_id ? [...demo_tabs, billing_tab] : demo_tabs} />

          <div style={{ padding: '40px 20px' }}>
            <Routes>
              <Route element={<General />} path='general' />
              <Route element={<Integrations />} path='integrations' />
              <Route element={<Products />} path='products' />
              <Route element={<AnnuityQuoter />} path='products/annuity_quoters/:id/*' />
              {agency?.agency_type === 'vive' ? (
                <Route element={<ViveShow />} path='products/api_clients/:id/*' />
              ) : (
                <Route element={<ApiClient />} path='products/api_clients/:id/*' />
              )}
              <Route element={<BgaQuoterShow />} path='products/bga_quoters/:id/*' />
              <Route element={<FormEnginesShow />} path='products/form_engines/:id/*' />
              <Route element={<LtcQuotersShow />} path='products/ltc_quoters/:id/*' />
              <Route element={<MobleAppShow />} path='products/mobile_apps/:id/*' />
              <Route element={<WebsiteQuotersShow />} path='products/website_quoters/:id/*' />
              <Route element={<Website />} path='products/websites/:id' />
              <Route element={<AgencyForms />} path='agency_forms' />
              <Route element={<LeadsList />} path='leads' />
              <Route element={<LeadsShow />} path='leads/:lead_id/*' />
              <Route element={<MembersList />} path='agency_members' />
              <Route element={<NotificationTriggers />} path='notification_triggers' />
              <Route element={<BillingShow />} path='billing/*' />
              <Route element={<UniversalQuoterShow />} path='products/universal_quoters/:id/*' />
            </Routes>
          </div>
        </Container>
      )}
    </AgencyContext.Provider>
  );
};

export default AgencyShow;
